import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Link as FluentLink,
  Spinner,
  MessageBar,
  MessageBarBody,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Toast,
  ToastTitle,
  ToastBody,
  useToastController,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Add24Regular,
  FolderOpen24Regular,
  MoreHorizontal20Regular,
  Delete20Regular,
  Archive20Regular,
  ArrowUndo20Regular,
  Search20Regular,
} from '@fluentui/react-icons';
import { Link, useNavigate } from 'react-router-dom';
import { ApiClientError } from '../api/client.js';
import {
  listProjects,
  deleteProject,
  patchProject,
  type Project,
  type ProjectIndexStatus,
} from '../api/projects.js';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { Chip } from '../components/Chip.js';
import { useConfirm } from '../components/ConfirmDialog.js';
import { formatoDeMoneda } from '../lib/money.js';
import { disciplinaLabel, estatusBadge } from '../lib/projectDisplay.js';
import { CreateProjectDialog } from '../components/CreateProjectDialog.js';
import { GLOBAL_TOASTER_ID } from '../components/ThemedProvider.js';
import { useAuthStore, useCanMutate } from '../stores/authStore.js';
import { useCanCreateProject } from '../stores/quotaGuards.js';
import { Tooltip } from '@fluentui/react-components';
import { productoActivo } from '../lib/productoActivo.js';

// Copy propio por producto: ai entra por documentos de licitación, ci por planos.
// Misma vista y diseño; solo cambia lo que se le dice al usuario. El producto lo
// decide el hostname (productoActivo).
const COPY_POR_PRODUCTO = {
  ai: {
    subtitulo: 'Cada proyecto es un concurso o licitación que estás cotizando.',
    vacioCrear: 'Crea el primero y sube los archivos de la licitación para empezar.',
  },
  ci: {
    subtitulo: 'Cada proyecto son los planos de una obra que estás convirtiendo en presupuesto.',
    vacioCrear: 'Crea el primero y sube tus planos (IFC, DWG o DXF) para empezar.',
  },
} as const;

function copyProyectos() {
  return COPY_POR_PRODUCTO[productoActivo()];
}

function NewProjectButton({
  onCreated,
  label = 'Nuevo proyecto',
}: {
  onCreated: (p: Project) => void;
  label?: string;
}) {
  const guard = useCanCreateProject();
  if (guard.allowed) {
    return (
      <CreateProjectDialog
        onCreated={onCreated}
        trigger={
          <Button appearance="primary" icon={<Add24Regular />} data-demo="nuevo-proyecto">
            {label}
          </Button>
        }
      />
    );
  }
  return (
    <Tooltip content={guard.message ?? 'Acción no permitida'} relationship="label">
      <Button appearance="primary" icon={<Add24Regular />} disabled>
        {label}
      </Button>
    </Tooltip>
  );
}

// ============================================================================
// Tipos locales + persistencia de preferencias del usuario
// ============================================================================

type Filter = 'active' | 'archived' | 'all';
type ViewMode = 'grid' | 'list';

const FILTER_KEY = 'supernova:projects-filter';
const VIEW_KEY = 'supernova:projects-view';
const AUTO_LIST_THRESHOLD = 10;

function readFilter(): Filter {
  const v = localStorage.getItem(FILTER_KEY);
  return v === 'archived' || v === 'all' ? v : 'active';
}
function readView(): ViewMode | null {
  const v = localStorage.getItem(VIEW_KEY);
  return v === 'grid' || v === 'list' ? v : null;
}

// ============================================================================
// Helpers de presentación
// ============================================================================

// Estado del proyecto en el vocabulario del mockup, derivado del progreso de
// cotización (no del estado de indexación). "Exportado" no se deriva del listado.
// Construye la meta de la card/lista: "disciplina · creado hace X" o solo "creado hace X".
function projectMeta(p: Project): string {
  // La disciplina se deduce del análisis del pliego (ai). En ci un proyecto es de
  // planos y no hay pliego: no se muestra en la card para no ensuciarla.
  const disc = productoActivo() === 'ci' ? '' : disciplinaLabel(p);
  const creado = `creado ${relativeTime(p.createdAt)}`;
  return disc ? `${disc} · ${creado}` : creado;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'hace un momento';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 14) return `hace ${d}d`;
  const w = Math.floor(d / 7);
  if (w < 8) return `hace ${w} sem`;
  const m = Math.floor(d / 30);
  if (m < 12) return `hace ${m} mes${m === 1 ? '' : 'es'}`;
  return `hace ${Math.floor(d / 365)} año(s)`;
}

// ============================================================================
// Styles
// ============================================================================

// El monto de cada proyecto va en LA MONEDA DE ESA OBRA (`p.moneda`), no en pesos
// mexicanos para todos: en una lista pueden convivir una obra chilena y una
// mexicana. Sin moneda conocida, el número va sin símbolo. Ver lib/money.ts.
const montoDeObra = (p: { quotedAmount?: number | null; moneda?: string | null }): string =>
  p.quotedAmount ? formatoDeMoneda(p.moneda).moneyCorto(p.quotedAmount) : '—';
const fmtNum = new Intl.NumberFormat('es-MX');

const useStyles = makeStyles({
  page: {},
  content: {
    padding: '26px',
    maxWidth: '1320px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-l)',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'var(--space-l)',
    flexWrap: 'wrap',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--fs-600)',
    fontWeight: 700,
    margin: 0,
    letterSpacing: '-0.02em',
  },
  pageSub: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--fs-200)',
    margin: '5px 0 0',
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-l)',
    flexWrap: 'wrap',
  },
  filterPills: {
    display: 'flex',
    gap: 'var(--space-s)',
    flexWrap: 'wrap',
  },
  pill: {
    height: '30px',
    padding: '0 13px',
    borderRadius: 'var(--radius-circular)',
    cursor: 'pointer',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-surface)',
    font: 'inherit',
    fontWeight: 600,
    transitionProperty: 'color, background, border-color',
    transitionDuration: '120ms',
    ':hover': {
      background: 'var(--bg-hover)',
      color: 'var(--text-primary)',
    },
  },
  pillActive: {
    background: 'var(--accent-weak)',
    border: '1px solid var(--accent)',
    color: 'var(--accent)',
  },
  pillCount: {
    fontFamily: 'var(--font-mono)',
    fontSize: '11px',
    fontWeight: 700,
    opacity: 0.85,
  },
  pillCountActive: {
    color: 'var(--accent)',
  },
  rightGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    marginLeft: 'auto',
    flexWrap: 'wrap',
  },
  searchWrap: { width: '260px', minWidth: '180px' },
  viewToggle: {
    display: 'flex',
    gap: 'var(--space-s)',
  },

  // ===== Grid =====
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
    gap: 'var(--space-l)',
  },
  card: {
    position: 'relative',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-card)',
    padding: '17px',
    display: 'flex',
    flexDirection: 'column',
    gap: '13px',
    textDecoration: 'none',
    color: 'inherit',
    transitionProperty: 'transform, border-color, box-shadow',
    transitionDuration: '150ms',
    ':hover': {
      transform: 'translateY(-2px)',
      border: '1px solid var(--border-default)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    },
  },
  cardArchived: {
    opacity: 0.55,
    ':hover': { opacity: 0.85 },
  },
  portada: {
    width: '100%',
    height: '120px',
    objectFit: 'cover',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-deep)',
    border: '1px solid var(--border-subtle)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'var(--space-s)',
  },
  cardName: {
    fontFamily: 'var(--font-display)',
    fontWeight: 650,
    fontSize: '15px',
    letterSpacing: '-0.2px',
    margin: 0,
    lineHeight: '1.45',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cardDesc: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    margin: 0,
    flex: 1,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cardMeta: {
    fontSize: '12px',
    color: 'var(--text-tertiary)',
    marginTop: '3px',
  },
  cardBadges: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  cardStats: {
    display: 'flex',
    gap: 'var(--space-l)',
    paddingTop: 'var(--space-m)',
    borderTop: '1px solid var(--border-subtle)',
  },
  stat: { display: 'flex', flexDirection: 'column', gap: '2px' },
  statValue: {
    fontWeight: 700,
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-primary)',
    fontSize: 'var(--fs-300)',
  },
  statLabel: {
    color: 'var(--text-tertiary)',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 600,
  },
  progress: {
    height: '5px',
    borderRadius: 'var(--radius-circular)',
    background: 'var(--bg-overlay)',
    overflow: 'hidden',
  },
  progressBar: { height: '100%', borderRadius: 'var(--radius-circular)', background: 'var(--accent)' },
  cardFoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-s)',
    marginTop: 'auto',
    paddingTop: 'var(--space-s)',
  },
  cardActivity: { fontSize: '11px', color: 'var(--text-tertiary)' },

  /** Insignia de estado como las del hub (`.app-st`): punto, mayúsculas y borde del color. */
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '3px 9px',
    borderRadius: 'var(--radius-circular)',
    border: '1px solid color-mix(in srgb, currentColor 35%, transparent)',
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    lineHeight: 1.3,
  },
  badgeDot: { width: '6px', height: '6px', borderRadius: '50%' },

  exampleBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
    marginTop: '4px',
    padding: '1px 8px',
    borderRadius: 'var(--radius-circular)',
    fontSize: '10px',
    fontFamily: 'var(--font-mono)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    background: 'rgba(12, 111, 214, 0.14)',
    color: '#3d8bff',
  },

  // ===== List =====
  list: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-card)',
    overflow: 'hidden',
  },
  listHeader: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr) 120px 90px 80px 120px 100px 40px',
    gap: 'var(--space-m)',
    padding: 'var(--space-m) var(--space-l)',
    background: 'var(--bg-raised)',
    borderBottom: '1px solid var(--border-subtle)',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontFamily: 'var(--font-mono)',
  },
  listRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr) 120px 90px 80px 120px 100px 40px',
    gap: 'var(--space-m)',
    padding: 'var(--space-m) var(--space-l)',
    alignItems: 'center',
    borderBottom: '1px solid var(--border-subtle)',
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    transitionProperty: 'background',
    transitionDuration: '100ms',
    ':hover': { background: 'var(--bg-raised)' },
    ':last-child': { borderBottom: 'none' },
  },
  listRowArchived: { opacity: 0.6 },
  listTitle: {
    fontWeight: 600,
    fontSize: 'var(--fs-200)',
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  listSub: {
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    fontFamily: 'var(--font-mono)',
    marginTop: '2px',
  },
  listMono: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-secondary)',
  },
  listNum: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-primary)',
    textAlign: 'right',
  },

  // ===== Misc =====
  empty: {
    background: 'var(--bg-surface)',
    border: '1px dashed var(--border-default)',
    borderRadius: 'var(--radius-card)',
    padding: '64px var(--space-l)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-m)',
    textAlign: 'center',
  },
  emptyIcon: {
    width: '56px',
    height: '56px',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--bg-raised)',
    display: 'grid',
    placeItems: 'center',
    color: 'var(--text-tertiary)',
  },
  emptyTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--fs-400)',
    fontWeight: 600,
    margin: 0,
  },
  emptyText: { color: 'var(--text-tertiary)', margin: 0 },
  emptyActions: { display: 'flex', gap: 'var(--space-s)', marginTop: 'var(--space-s)' },

  loading: { minHeight: '300px', display: 'grid', placeItems: 'center' },
});

// ============================================================================
// Sub-components
// ============================================================================

function StatusBadge({ project }: { project: Project }) {
  const styles = useStyles();
  const s = estatusBadge(project);
  return (
    <span className={styles.badge} style={{ background: s.bg, color: s.fg }}>
      <span className={styles.badgeDot} style={{ background: 'currentColor' }} />
      {s.label}
    </span>
  );
}

function ProjectActionsMenu({
  project,
  onArchive,
  onUnarchive,
  onDelete,
}: {
  project: Project;
  onArchive: (p: Project) => void;
  onUnarchive: (p: Project) => void;
  onDelete: (p: Project) => void;
}) {
  // Viewer = solo consulta; no expone acciones de mutación.
  const canMutate = useCanMutate();
  // El proyecto de ejemplo (demo) es una fila COMPARTIDA entre todos los tenants:
  // archivarlo/eliminarlo afectaría a todos. Sin menú de acciones.
  if (!canMutate || project.isExample) return null;
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          size="small"
          icon={<MoreHorizontal20Regular />}
          aria-label="Acciones del proyecto"
          onClick={(e) => e.preventDefault()}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {project.status === 'archived' ? (
            <MenuItem
              icon={<ArrowUndo20Regular />}
              onClick={(e) => {
                e.preventDefault();
                onUnarchive(project);
              }}
            >
              Restaurar
            </MenuItem>
          ) : (
            <MenuItem
              icon={<Archive20Regular />}
              onClick={(e) => {
                e.preventDefault();
                onArchive(project);
              }}
            >
              Archivar
            </MenuItem>
          )}
          <MenuItem
            icon={<Delete20Regular />}
            onClick={(e) => {
              e.preventDefault();
              onDelete(project);
            }}
          >
            Eliminar
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

// ============================================================================
// Main page
// ============================================================================

export function ProjectsPage() {
  const styles = useStyles();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const tenant = useAuthStore((s) => s.tenant);
  const canMutate = useCanMutate();
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);

  const [projects, setProjects] = useState<Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>(readFilter);
  const [search, setSearch] = useState('');
  const [userView, setUserView] = useState<ViewMode | null>(readView);

  const refresh = useCallback(async () => {
    try {
      setProjects(await listProjects());
      setError(null);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError('Error cargando proyectos');
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [tenant?.id, refresh]);

  // Persistencia de preferencias
  useEffect(() => { localStorage.setItem(FILTER_KEY, filter); }, [filter]);
  useEffect(() => {
    if (userView) localStorage.setItem(VIEW_KEY, userView);
  }, [userView]);

  // Conteos por estado (excluye 'deleted' que ya viene filtrado del backend)
  const counts = useMemo(() => {
    if (!projects) return { active: 0, archived: 0, all: 0 };
    let active = 0, archived = 0;
    for (const p of projects) {
      if (p.status === 'archived') archived++;
      else active++;
    }
    return { active, archived, all: active + archived };
  }, [projects]);

  // Vista efectiva: auto-list cuando hay muchos proyectos y el usuario no eligió
  const view: ViewMode = userView ?? (counts.all >= AUTO_LIST_THRESHOLD ? 'list' : 'grid');

  // Filtrado por estado + búsqueda
  const visibleProjects = useMemo(() => {
    if (!projects) return [];
    const q = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (filter === 'active' && p.status !== 'active') return false;
      if (filter === 'archived' && p.status !== 'archived') return false;
      if (q && !p.name.toLowerCase().includes(q) && !(p.description ?? '').toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [projects, filter, search]);

  // ===== Acciones =====

  async function onArchive(p: Project) {
    try {
      await patchProject(p.id, { status: 'archived' });
      setProjects((prev) =>
        prev?.map((x) => (x.id === p.id ? { ...x, status: 'archived' } : x)) ?? null,
      );
      dispatchToast(
        <Toast>
          <ToastTitle
            action={
              <FluentLink onClick={() => void undoArchive(p)} appearance="subtle">
                Deshacer
              </FluentLink>
            }
          >
            "{p.name}" archivado
          </ToastTitle>
          <ToastBody>El proyecto se ocultó de la vista activa.</ToastBody>
        </Toast>,
        { intent: 'success', timeout: 6000 },
      );
    } catch (err) {
      dispatchToast(
        <Toast>
          <ToastTitle>No se pudo archivar</ToastTitle>
          <ToastBody>{(err as Error).message}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    }
  }

  async function undoArchive(p: Project) {
    try {
      await patchProject(p.id, { status: 'active' });
      setProjects((prev) =>
        prev?.map((x) => (x.id === p.id ? { ...x, status: 'active' } : x)) ?? null,
      );
    } catch (err) {
      dispatchToast(
        <Toast>
          <ToastTitle>No se pudo deshacer</ToastTitle>
          <ToastBody>{(err as Error).message}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    }
  }

  async function onUnarchive(p: Project) {
    try {
      await patchProject(p.id, { status: 'active' });
      setProjects((prev) =>
        prev?.map((x) => (x.id === p.id ? { ...x, status: 'active' } : x)) ?? null,
      );
      dispatchToast(
        <Toast>
          <ToastTitle>"{p.name}" restaurado</ToastTitle>
        </Toast>,
        { intent: 'success', timeout: 3000 },
      );
    } catch (err) {
      dispatchToast(
        <Toast>
          <ToastTitle>No se pudo restaurar</ToastTitle>
          <ToastBody>{(err as Error).message}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    }
  }

  async function onDelete(p: Project) {
    if (!(await confirm({
      title: 'Eliminar proyecto',
      message: `¿Eliminar el proyecto "${p.name}"?\nEsta acción es permanente y borra todos los archivos.`,
      confirmLabel: 'Eliminar',
      destructive: true,
    }))) return;
    try {
      await deleteProject(p.id);
      setProjects((prev) => prev?.filter((x) => x.id !== p.id) ?? null);
    } catch (err) {
      dispatchToast(
        <Toast>
          <ToastTitle>No se pudo eliminar</ToastTitle>
          <ToastBody>{(err as Error).message}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    }
  }

  // ===== Render =====

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs items={[{ label: 'Inicio', to: '/projects' }, { label: 'Proyectos' }]} />
      <main className={styles.content}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Proyectos</h1>
            <p className={styles.pageSub}>{copyProyectos().subtitulo}</p>
          </div>
          <NewProjectButton onCreated={(p) => navigate(`/project/${p.id}`)} />
        </div>

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {projects === null ? (
          <div className={styles.loading}>
            <Spinner label="Cargando proyectos…" />
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className={styles.toolbar}>
              <div className={styles.filterPills} role="radiogroup" aria-label="Filtro por estado">
                {(['active', 'archived', 'all'] as const).map((f) => (
                  <Chip
                    key={f}
                    label={f === 'active' ? 'Activos' : f === 'archived' ? 'Archivados' : 'Todos'}
                    count={counts[f]}
                    active={filter === f}
                    onClick={() => setFilter(f)}
                  />
                ))}
              </div>

              <div className={styles.rightGroup}>
              <div className={styles.searchWrap}>
                <Input
                  placeholder="Buscar en proyectos…"
                  value={search}
                  onChange={(_, d) => setSearch(d.value)}
                  contentBefore={<Search20Regular style={{ color: 'var(--text-tertiary)' }} />}
                />
              </div>

              <div className={styles.viewToggle} role="radiogroup" aria-label="Vista">
                <Chip label="Cuadrícula" active={view === 'grid'} onClick={() => setUserView('grid')} />
                <Chip label="Lista" active={view === 'list'} onClick={() => setUserView('list')} />
              </div>
              </div>
            </div>

            {/* Contenido */}
            {counts.all === 0 ? (
              <FirstEmptyState onCreate={(p) => navigate(`/project/${p.id}`)} />
            ) : visibleProjects.length === 0 ? (
              <FilteredEmptyState
                filter={filter}
                counts={counts}
                onClearSearch={() => setSearch('')}
                onChangeFilter={setFilter}
                hasSearch={search.trim().length > 0}
              />
            ) : view === 'grid' ? (
              <GridView
                projects={visibleProjects}
                onArchive={onArchive}
                onUnarchive={onUnarchive}
                onDelete={onDelete}
              />
            ) : (
              <ListView
                projects={visibleProjects}
                onArchive={onArchive}
                onUnarchive={onUnarchive}
                onDelete={onDelete}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ============================================================================
// Grid + List + Empty subcomponents
// ============================================================================

function GridView({
  projects,
  onArchive,
  onUnarchive,
  onDelete,
}: {
  projects: Project[];
  onArchive: (p: Project) => void;
  onUnarchive: (p: Project) => void;
  onDelete: (p: Project) => void;
}) {
  const styles = useStyles();
  return (
    <div className={styles.grid}>
      {projects.map((p) => (
        <Link
          key={p.id}
          to={`/project/${p.id}`}
          className={mergeClasses(styles.card, p.status === 'archived' && styles.cardArchived)}
        >
          {p.portadaUrl && (
            <img
              className={styles.portada}
              src={p.portadaUrl}
              alt=""
              loading="lazy"
              onError={(e) => {
                // La vista previa es un adorno: si no carga (permiso, borrada), la tarjeta queda sin ella.
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div className={styles.cardHeader}>
            <div style={{ minWidth: 0 }}>
              <h2 className={styles.cardName}>{p.name}</h2>
              {p.isExample && <span className={styles.exampleBadge}>Ejemplo</span>}
              <div className={styles.cardMeta}>{projectMeta(p)}</div>
            </div>
            <div onClick={(e) => e.preventDefault()}>
              <ProjectActionsMenu
                project={p}
                onArchive={onArchive}
                onUnarchive={onUnarchive}
                onDelete={onDelete}
              />
            </div>
          </div>
          {p.description && <p className={styles.cardDesc}>{p.description}</p>}
          {p.quotedPct != null && (
            <div className={styles.progress}>
              <div className={styles.progressBar} style={{ width: `${p.quotedPct}%` }} />
            </div>
          )}
          <div className={styles.cardStats}>
            <div className={styles.stat}>
              <span className={styles.statValue}>{p.quoteConcepts != null ? fmtNum.format(p.quoteConcepts) : '—'}</span>
              <span className={styles.statLabel}>conceptos</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{p.quotedPct != null ? `${p.quotedPct}%` : '—'}</span>
              <span className={styles.statLabel}>cotizado</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{montoDeObra(p)}</span>
              <span className={styles.statLabel}>monto</span>
            </div>
          </div>
          <div className={styles.cardFoot}>
            <StatusBadge project={p} />
            <span className={styles.cardActivity}>{relativeTime(p.updatedAt)}</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

function ListView({
  projects,
  onArchive,
  onUnarchive,
  onDelete,
}: {
  projects: Project[];
  onArchive: (p: Project) => void;
  onUnarchive: (p: Project) => void;
  onDelete: (p: Project) => void;
}) {
  const styles = useStyles();
  return (
    <div className={styles.list}>
      <div className={styles.listHeader}>
        <span>Proyecto</span>
        <span>Estado</span>
        <span style={{ textAlign: 'right' }}>Conceptos</span>
        <span style={{ textAlign: 'right' }}>Presupuestado</span>
        <span style={{ textAlign: 'right' }}>Monto</span>
        <span>Última actividad</span>
        <span />
      </div>
      {projects.map((p) => (
        <Link
          key={p.id}
          to={`/project/${p.id}`}
          className={mergeClasses(styles.listRow, p.status === 'archived' && styles.listRowArchived)}
        >
          <div style={{ minWidth: 0 }}>
            <div className={styles.listTitle}>
              {p.name}
              {p.isExample && (
                <span className={styles.exampleBadge} style={{ marginTop: 0, marginLeft: '8px', verticalAlign: 'middle' }}>
                  Ejemplo
                </span>
              )}
            </div>
            <div className={styles.listSub}>{projectMeta(p)}</div>
          </div>
          <StatusBadge project={p} />
          <span className={styles.listNum}>{p.quoteConcepts != null ? fmtNum.format(p.quoteConcepts) : '—'}</span>
          <span className={styles.listNum}>{p.quotedPct != null ? `${p.quotedPct}%` : '—'}</span>
          <span className={styles.listNum}>{montoDeObra(p)}</span>
          <span className={styles.listMono}>{relativeTime(p.updatedAt)}</span>
          <div onClick={(e) => e.preventDefault()}>
            <ProjectActionsMenu
              project={p}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
              onDelete={onDelete}
            />
          </div>
        </Link>
      ))}
    </div>
  );
}

function FirstEmptyState({ onCreate }: { onCreate: (p: Project) => void }) {
  const styles = useStyles();
  const canMutate = useCanMutate();
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>
        <FolderOpen24Regular />
      </div>
      <div>
        <h3 className={styles.emptyTitle}>Aún no tienes proyectos</h3>
        <p className={styles.emptyText} style={{ marginTop: 8 }}>
          {canMutate
            ? copyProyectos().vacioCrear
            : 'Tu cuenta es de solo consulta. Pide a un administrador que cree el primer proyecto.'}
        </p>
      </div>
      <NewProjectButton onCreated={onCreate} label="Crear primer proyecto" />
    </div>
  );
}

function FilteredEmptyState({
  filter,
  counts,
  onClearSearch,
  onChangeFilter,
  hasSearch,
}: {
  filter: Filter;
  counts: { active: number; archived: number; all: number };
  onClearSearch: () => void;
  onChangeFilter: (f: Filter) => void;
  hasSearch: boolean;
}) {
  const styles = useStyles();
  if (hasSearch) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyIcon}>
          <Search20Regular />
        </div>
        <h3 className={styles.emptyTitle}>Sin resultados</h3>
        <p className={styles.emptyText}>Ningún proyecto coincide con tu búsqueda.</p>
        <div className={styles.emptyActions}>
          <Button onClick={onClearSearch}>Limpiar búsqueda</Button>
        </div>
      </div>
    );
  }
  if (filter === 'active') {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyIcon}>
          <Archive20Regular />
        </div>
        <h3 className={styles.emptyTitle}>No tienes proyectos activos</h3>
        <p className={styles.emptyText}>
          Tienes <strong>{counts.archived} proyecto{counts.archived === 1 ? '' : 's'} archivado{counts.archived === 1 ? '' : 's'}</strong> disponible{counts.archived === 1 ? '' : 's'} para consulta.
        </p>
        <div className={styles.emptyActions}>
          <Button onClick={() => onChangeFilter('archived')}>Ver archivados</Button>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>
        <Archive20Regular />
      </div>
      <h3 className={styles.emptyTitle}>Sin proyectos archivados</h3>
      <p className={styles.emptyText}>Cuando archives un proyecto aparecerá aquí.</p>
      <div className={styles.emptyActions}>
        <Button onClick={() => onChangeFilter('active')}>Ver activos</Button>
      </div>
    </div>
  );
}
